import React, {
  forwardRef, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import dayjs from 'dayjs';
import {
  CloseIcon, Dragndrop, TriangleArrowDown,
} from '@picsio/icons';
import { IconButton, Popper } from '@picsio/ui';
import SearchFilterDropOpener from './SearchFilterDropOpener';
import { MULTIPLE_OPERATORS, SINGLE_OPERATORS } from './config';

import Tooltip from '../Tooltip';
import getBaseTitle from './helpers/getBaseTitle';
import getDateTitle from './helpers/getDateTitle';
import getRatingTitle from './helpers/getRatingTitle';
import getSizeTitle from './helpers/getSizeTitle';
import getArrayValueTitle from './helpers/getArrayValueTitle';
import getComplexArrayValueTitle from './helpers/getComplexArrayValueTitle';
import getBooleanTitle from './helpers/getBooleanTitle';
import getNumberTitle from './helpers/getNumberTitle';
import getCollectionTitle from './helpers/getCollectionTitle';
import normalizeDate from './helpers/normalizeDate';
import normalizeText from './helpers/normalizeText';
import { deserializeDateForPicker } from './helpers/searchDateSerializer';

const SearchFilterItem = forwardRef(({
  id = '',
  disabled,
  data,
  draggable = true,
  onDragStart,
  onDragEnter,
  onDragEnd,
  customizingMode,
  isSelected,
  isOpen,
  onOpen,
  onClose,
  onSelectFilter,
  onResetFilter,
  onRemove,
  filterData,
  onConfirmFilter,
  confirmedData = {},
  filters,
  rootCollectionId,
}, ref) => {
  const {
    title, type, subtype, operators, options,
  } = data;

  const operatorName = useMemo(() => {
    if (id.startsWith('meta.')) {
      if (type === 'number') {
        return filters?.meta?.operatorNamesNumber[confirmedData?.operator];
      }
      if (type === 'date') {
        return filters?.meta?.operatorNamesDate[confirmedData?.operator];
      }
      return filters?.meta?.operatorNamesString[confirmedData?.operator];
    }
    if (filters[id]?.operatorNames) {
      return filters[id]?.operatorNames[confirmedData?.operator];
    }

    return confirmedData?.operator;
  }, [confirmedData?.operator, filters, id, type]);

  const useUtcDates = useMemo(
    () => id.startsWith('meta.') && type === 'date' && confirmedData.operator === 'equals',
    [id, type, confirmedData.operator],
  );

  const formattedSelectedFilterTooltip = useMemo(() => {
    const titleToRender = title.startsWith('meta.') ? title.replace('meta.', '') : title;
    if (isSelected) {
      let selectedValueTitle = '';
      if (Array.isArray(confirmedData.value) && !SINGLE_OPERATORS.includes(confirmedData.operator)) {
        if (confirmedData.value[0]?.name) {
          selectedValueTitle = confirmedData.value.map((item) => normalizeText(item.name)).join(', ');
        } else if (confirmedData.value[0]?.title) {
          selectedValueTitle = confirmedData.value.map((item) => normalizeText(item.title)).join(', ');
        } else if (confirmedData.value[0]?.displayName) {
          selectedValueTitle = confirmedData.value.map((item) => normalizeText(item.displayName)).join(', ');
        } else {
          selectedValueTitle = confirmedData.value.map((item) => normalizeText(item)).join(', ');
        }
        if (id === 'fileSize') {
          return `${titleToRender} (${operatorName}) ${getSizeTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}`;
        }
        if (type === 'number' || type === 'float') {
          return `${titleToRender} (${operatorName}) ${getNumberTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}`;
        }
        if (type === 'rating') {
          return `${titleToRender} (${operatorName}) ${getRatingTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}`;
        }
        if (!MULTIPLE_OPERATORS.includes(confirmedData.operator)) {
          return `${titleToRender} (${operatorName}) ${selectedValueTitle}`;
        }
      }
      if (MULTIPLE_OPERATORS.includes(confirmedData.operator)) {
        if (!operatorName) return '';
        const formattedOperatorTitle = operatorName?.split('...');
        let excludedValueTitle = '';
        if (confirmedData.excludedValues && confirmedData.excludedValues.length > 0) {
          if (confirmedData.excludedValues[0]?.name) {
            excludedValueTitle = confirmedData.excludedValues.map((item) => normalizeText(item.name)).join(', ');
          } else if (confirmedData.excludedValues[0]?.title) {
            excludedValueTitle = confirmedData.excludedValues.map((item) => normalizeText(item.title)).join(', ');
          } else if (confirmedData.excludedValues[0]?.displayName) {
            excludedValueTitle = confirmedData.excludedValues.map((item) => normalizeText(item.displayName)).join(', ');
          } else {
            excludedValueTitle = confirmedData.excludedValues.map((item) => normalizeText(item)).join(', ');
          }
          if (selectedValueTitle.length === 0) {
            return `${titleToRender} (${formattedOperatorTitle[1]?.trim()}) ${excludedValueTitle}`;
          }
          if (excludedValueTitle.length === 0) {
            return `${titleToRender} (${operatorName}) ${selectedValueTitle}`;
          }
          return `${titleToRender} (${formattedOperatorTitle[0]}) ${selectedValueTitle} (${formattedOperatorTitle[1]?.trim()}) ${excludedValueTitle}`;
        }
        return `${titleToRender} (${formattedOperatorTitle[0]}) ${selectedValueTitle}`;
      }

      if (type === 'date') {
        selectedValueTitle = new Date(confirmedData.value).toLocaleDateString();

        if (useUtcDates) {
          selectedValueTitle = dayjs(deserializeDateForPicker(confirmedData.value, true)).format('DD MMM YYYY');
        } else if (confirmedData.operator === 'between') {
          const dates = confirmedData.value.split('_');
          selectedValueTitle = `${normalizeDate(dates[0])} - ${normalizeDate(dates[1], true)}`;
        } else {
          selectedValueTitle = normalizeDate(confirmedData.value);
        }

        const cleanedTitle = titleToRender.replace(/\bat\b/g, '').trim();
        return `${cleanedTitle} ${operatorName} ${SINGLE_OPERATORS.includes(confirmedData.operator) ? '' : selectedValueTitle}`;
      }
      if (type === 'boolean' && !SINGLE_OPERATORS.includes(confirmedData.operator)) { // PCS-8431 - statuses should be as (Trashed Status: Trashed) and so on.
        selectedValueTitle = options.find((option) => option.value === confirmedData.value)?.label;
        return `${titleToRender}: ${selectedValueTitle}`;
      }
      return `${titleToRender} (${operatorName}) ${SINGLE_OPERATORS.includes(confirmedData.operator) ? '' : normalizeText(confirmedData.value)}`;
    }
    return titleToRender;
  }, [title, isSelected, confirmedData, type, operatorName, id, options, useUtcDates]);

  const formattedSelectedFilterTitle = useMemo(() => {
    const titleToRender = title.startsWith('meta.') ? title.replace('meta.', '') : title;

    if (!isSelected) {
      return <span className="searchFilterItem__subtitle">{titleToRender}</span>;
    }

    const filterTypeMap = {
      options: () => (
        <span data-type="options" className="searchFilterItem__subtitle">
          {getBaseTitle({
            text: options.find((option) => option.value === confirmedData.value)?.label,
            operator: confirmedData.operator,
            isSingle: true,
          })}
        </span>
      ),
      fileSize: () => (
        <span className="searchFilterItem__subtitle">
          {getSizeTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}
        </span>
      ),
      date: () => (
        <span className="searchFilterItem__subtitle">
          {useUtcDates
            ? dayjs(deserializeDateForPicker(confirmedData.value, true)).format('DD MMM YYYY')
            : getDateTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}
        </span>
      ),
      boolean: () => {
        const selectedValueTitle = options.find((option) => option.value === confirmedData.value)?.label;
        return (
          <span data-type="boolean" className="searchFilterItem__subtitle">
            {getBooleanTitle({
              text: titleToRender, operator: confirmedData.operator, value: confirmedData.value, label: selectedValueTitle,
            })}
          </span>
        );
      },
      rating: () => (
        <span className="searchFilterItem__subtitle">
          {getRatingTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}
        </span>
      ),
      number: () => (
        <span className="searchFilterItem__subtitle">
          {getNumberTitle({ operator: confirmedData.operator, value: confirmedData.value, title: titleToRender })}
        </span>
      ),
      collection: () => getCollectionTitle({ confirmedData, rootCollectionId }),
      array: () => getArrayValueTitle(confirmedData),
      complexArray: () => getComplexArrayValueTitle(confirmedData),
      default: () => (
        <span data-type="default" className="searchFilterItem__subtitle">
          {SINGLE_OPERATORS.includes(confirmedData.operator)
            ? `${titleToRender} ${operatorName?.replace(/\bis\b/g, '').trim()}`
            : getBaseTitle({ text: confirmedData.value, operator: confirmedData.operator, isSingle: true })}
        </span>
      ),
    };

    if (id === 'fileSize') return filterTypeMap.fileSize();
    if (type === 'date') return filterTypeMap.date();
    if (type === 'boolean') return filterTypeMap.boolean();
    if (type === 'rating') return filterTypeMap.rating();
    if (type === 'number') return filterTypeMap.number();
    if (type === 'float') return filterTypeMap.number();
    if (type === 'collectionIds') return filterTypeMap.collection();
    if (MULTIPLE_OPERATORS.includes(confirmedData.operator)) {
      return filterTypeMap.complexArray();
    }
    if (Array.isArray(confirmedData.value) && !SINGLE_OPERATORS.includes(confirmedData.operator)) {
      return filterTypeMap.array();
    }
    if (options) return filterTypeMap.options();

    return filterTypeMap.default();
  }, [confirmedData, isSelected, operatorName, title, type, options, id, rootCollectionId, useUtcDates]);

  const handleOpen = useCallback(() => (customizingMode ? Function.prototype : onOpen(id)), [customizingMode, id, onOpen]);

  const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

  const handleResetFilter = useCallback((e) => {
    e.stopPropagation();
    onResetFilter(id);
  }, [id, onResetFilter]);

  const active = useMemo(() => {
    if (isSelected && !customizingMode) {
      if (id === 'collectionIds' && confirmedData?.value?.length === 1 && confirmedData?.value[0]._id === rootCollectionId) {
        return false;
      }
      return true;
    }
    if (isOpen && !customizingMode) {
      return true;
    }
    return false;
  }, [confirmedData?.value, customizingMode, id, isOpen, isSelected, rootCollectionId]);

  const handleChipHover = (e, shouldAdd = true) => {
    if (!active) {
      e.currentTarget.classList.remove('is-hover');
      return;
    }
    const isIcon = e.target.closest('.searchFilterItem__resetIcon');
    e.currentTarget.classList[isIcon || !shouldAdd ? 'remove' : 'add']('is-hover');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (isOpen) {
        onClose(e);
      } else {
        handleOpen(e);
      }
    }
  };

  return (
    <div
      key={id}
      data-id={id}
      draggable={!disabled && draggable && customizingMode}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      style={{
        position: 'relative', display: 'inline-block', marginBottom: '4px', marginTop: '4px',
      }}
      ref={ref}
    >

      <div
        className={cn('searchFilterItem', {
          settingMode: customizingMode,
          active,
          checked: confirmedData.value,
        })}
        onClick={isOpen ? onClose : handleOpen}
        onMouseOver={(e) => handleChipHover(e, true)}
        onMouseOut={(e) => handleChipHover(e, false)}
        onFocus={(e) => handleChipHover(e, true)}
        onBlur={(e) => handleChipHover(e, false)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <If condition={customizingMode && draggable}>
          <div className="searchFilterItem__section">
            <Dragndrop className="searchFilterItem__dnd" />
          </div>
        </If>
        <Tooltip placement="top" content={isSelected ? formattedSelectedFilterTooltip : ''}>
          <div
            className={cn('searchFilterItem__title')}
          >
            { formattedSelectedFilterTitle }
          </div>
        </Tooltip>
        <div className="searchFilterItem__section">
          <Choose>
            <When condition={customizingMode}>
              <IconButton size="sm" disabled={disabled} className="searchFilterItem__iconBtn" onClick={handleRemove}>
                <CloseIcon className="searchFilterItem__closeIcon" />
              </IconButton>
            </When>
            <Otherwise>
              <Choose>
                <When condition={active && confirmedData.value}>
                  <Tooltip placement="top" content="Reset filter">
                    <IconButton className="searchFilterItem__resetIcon" color="primary" size="xs" disabled={disabled} onClick={handleResetFilter}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </When>
                <Otherwise>
                  <IconButton size="sm" disabled={disabled} className="searchFilterItem__iconBtn">
                    <TriangleArrowDown />
                  </IconButton>
                </Otherwise>
              </Choose>
            </Otherwise>
          </Choose>
        </div>
      </div>
      <If condition={ref}>
        <Popper
          target={ref}
          className="dropdownElementPopper"
          isOpen={isOpen}
          onClose={() => {}}
          offset={[0, 10]}
          hide={false}
          arrow={false}
          autoWidth
          outsideClickListener
        >
          <SearchFilterDropOpener
            btnRef={ref}
            id={id}
            type={type}
            subtype={subtype}
            selectedFilterData={filterData}
            confirmedData={confirmedData}
            isSelected={isSelected}
            isDropdownOpened={isOpen}
            operators={operators}
            options={options}
            onSelectFilter={onSelectFilter}
            onConfirmFilter={onConfirmFilter}
            onResetFilter={onResetFilter}
            onCloseDropdown={onClose}
            data={data}
            filters={filters}
            title={title}
          />
        </Popper>
      </If>
    </div>
  );
});

SearchFilterItem.propTypes = {
  id: PropTypes.string,
  disabled: PropTypes.bool,
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subtype: PropTypes.string,
    operators: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      label: PropTypes.string,
    })),
  }).isRequired,
  draggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragEnter: PropTypes.func,
  onDragEnd: PropTypes.func,
  customizingMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onSelectFilter: PropTypes.func,
  onResetFilter: PropTypes.func,
  onRemove: PropTypes.func,
  filterData: PropTypes.object,
  onConfirmFilter: PropTypes.func,
  confirmedData: PropTypes.shape({
    operator: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
  }),
  filters: PropTypes.object,
  rootCollectionId: PropTypes.string,
};

export default SearchFilterItem;
